<script setup lang="ts">
const logoSrc = '/images/logos/logo-footer.svg'
const email = ref('')
const status = ref<'idle' | 'loading' | 'success' | 'error' | 'empty'>('idle')

// Replace with your actual Klaviyo List ID
// Found in Klaviyo → Audience → Lists → your list → Settings
const KLAVIYO_LIST_ID = 'VaNEqT'
const KLAVIYO_COMPANY_ID = 'XArjzR' // Found in Klaviyo → Settings → Account

async function handleSubscribe() {
  if (status.value === 'loading') return
  if (!email.value) {
    status.value = 'empty'
    return
  }
 
  status.value = 'loading'
 
  try {
    await $fetch('/api/klaviyo/subscribe', {
      method: 'POST',
      body: { email: email.value },
    })
 
    status.value = 'success'
    email.value = ''
    await navigateTo('/thank-you')
  } catch {
    status.value = 'error'
  }
}
</script>

<template>
  <footer class="bg-dark">
    <!-- Main content -->
    <div class="px-4 md:px-20 pt-14 pb-10">
      <!-- Desktop layout -->
      <div class="hidden md:flex items-center gap-4 h-[219px]">
        <!-- Left — Brand -->
        <div class="flex flex-col gap-3 flex-1 self-stretch">
          <img
            :src="logoSrc"
            alt="Wet Water"
            class="h-[73px] w-[88px] object-contain self-start"
          />
          <p class="font-serif text-xl leading-7 text-brand">
            A journal of beauty, land and craft.
          </p>
        </div>

        <!-- Center — Newsletter -->
        <div id="footer-subscribe" class="flex flex-col gap-2 w-[315px] justify-center self-stretch">
          <p class="font-serif text-xl leading-7 text-muted text-right">
            JOIN OUR MAILING LIST TO RECEIVED NEW STORIES AND NEWS
          </p>
          <div class="flex flex-col items-end gap-2">
            <input
              v-model="email"
              type="email"
              placeholder="Your email address"
              :disabled="status === 'loading'"
              @input="status === 'empty' && (status = 'idle')"
              class="flex-1 w-full border border-secondary rounded px-3 py-2 bg-transparent font-sans font-light text-xs leading-4 text-inverse placeholder:text-disabled outline-none focus:border-brand transition-colors disabled:opacity-50"
            />
            <p v-if="status === 'empty'" class="font-sans font-light text-xs text-error self-start">
              Please enter your email address.
            </p>
            <p v-else-if="status === 'error'" class="font-sans font-light text-xs text-error self-start">
              Something went wrong. Please try again.
            </p>
            <AppButton
              type="Primary"
              size="Small"
              :label="status === 'loading' ? 'JOINING...' : 'JOIN'"
              class="!bg-brand w-1/2"
              :disabled="status === 'loading'"
              @click="handleSubscribe"
            />
          </div>
        </div>

        <!-- Right — Hero image -->
        <div class="flex-1 self-stretch relative overflow-hidden">
          <img
            src="/images/editorial/footer-hero.png"
            alt=""
            class="absolute inset-0 w-full h-full object-cover"
          />
          <div class="absolute inset-0 bg-black/30" />
        </div>
      </div>

      <!-- Mobile layout -->
      <div class="flex flex-col gap-8 md:hidden">
        <!-- Brand -->
        <div class="flex flex-col items-center text-center gap-3">
          <img
            :src="logoSrc"
            alt="Wet Water"
            class="h-[56px] w-[72px] object-contain"
          />
          <p class="font-serif text-xl leading-7 text-brand">
            A journal of beauty, land and craft.
          </p>
        </div>

        <!-- Hero image -->
        <div class="relative h-48 overflow-hidden">
          <img
            src="/images/editorial/footer-hero.png"
            alt=""
            class="absolute inset-0 w-full h-full object-cover"
          />
          <div class="absolute inset-0 bg-black/30" />
        </div>

        <!-- Newsletter -->
        <div id="footer-subscribe" class="flex flex-col gap-8">
          <p class="font-serif text-lg leading-7 text-muted text-center">
            JOIN OUR MAILING LIST TO RECEIVED NEW STORIES AND NEWS
          </p>
          <div class="flex flex-col items-end gap-4">
            <input
              v-model="email"
              type="email"
              placeholder="Your email address"
              :disabled="status === 'loading'"
              @input="status === 'empty' && (status = 'idle')"
              class="flex-1 w-full border border-secondary rounded px-3 py-3 bg-transparent font-sans font-light text-xs leading-4 text-inverse placeholder:text-disabled outline-none focus:border-brand transition-colors disabled:opacity-50"
            />
            <p v-if="status === 'empty'" class="font-sans font-light text-xs text-error self-start">
              Please enter your email address.
            </p>
            <p v-else-if="status === 'error'" class="font-sans font-light text-xs text-error self-start">
              Something went wrong. Please try again.
            </p>
            <AppButton
              type="Primary"
              size="Medium"
              :label="status === 'loading' ? 'JOINING...' : 'JOIN'"
              class="!bg-brand w-1/2"
              :disabled="status === 'loading'"
              @click="handleSubscribe"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="border-t border-secondary mx-4 md:mx-20 pt-6 pb-10 flex flex-col md:flex-row items-center justify-between gap-4">
      <p class="font-sans font-light text-xs leading-4 text-muted">
        &copy; 2026 Wet Water. All rights reserved.
      </p>

      <nav class="flex gap-20">
        <NuxtLink
          to="/journal"
          class="font-label text-lg text-brand hover:text-inverse transition-colors"
        >
          MAGAZINE
        </NuxtLink>
        <NuxtLink
          to="/shop"
          class="font-label text-lg text-brand hover:text-inverse transition-colors"
        >
          GOODS
        </NuxtLink>
      </nav>

      <nav class="flex gap-6 items-center">
        <NuxtLink
          to="/about"
          class="font-sans font-light text-xs leading-4 text-muted hover:text-inverse transition-colors"
        >
          About
        </NuxtLink>
        <NuxtLink
          to="/contact"
          class="font-sans font-light text-xs leading-4 text-muted hover:text-inverse transition-colors"
        >
          Contact
        </NuxtLink>
        <NuxtLink
          to="/privacy"
          class="font-sans font-light text-xs leading-4 text-muted hover:text-inverse transition-colors"
        >
          Privacy & Term of Service
        </NuxtLink>
      </nav>
    </div>
  </footer>
</template>